import React, { useContext } from 'react';

import moment from 'moment';

import {
  isAnnounced,
  isFundRaising,
  Money as MoneyModel,
  ProjectStatus,
} from '../../../../api-client';
import { formatPercents } from '../../../../formatters';
import { declOfNum } from '../../../../utils';
import { calculateCollectedPercentage } from '../../../../utils/pages';
import { Money } from '../../../common';
import ProjectProgress from '../../projects/ProjectProgress';
import { DetailedProjectContext } from '../DetailedProjectContext/DetailedProjectContext';
import { useWindowWidth } from '../hooks';
import styles from './ProjectMainStats.module.scss';

const ProjectMainStats = () => {
  const project = useContext(DetailedProjectContext).project;
  const isMobile = useWindowWidth(1260);

  const extraCreditorPaymentForecast =
    project.accounting.extraCreditorPaymentForecast ??
    new MoneyModel(0, project.accounting.finalDebtAmount.currencyCode);

  const recoveryForecast = new MoneyModel(
    project.accounting.finalDebtAmount.amount - extraCreditorPaymentForecast.amount,
    project.accounting.finalDebtAmount.currencyCode
  );
  const isLoanPaymentPaid =
    project.status === ProjectStatus.LOAN_PAYMENT || project.status === ProjectStatus.PAID;
  const isPaid = project.status === ProjectStatus.PAID;

  const formatDays = (days: number): string => {
    const d = Math.max(0, days);
    return `${d} ${declOfNum(d, ['день', 'дня', 'дней'])}`;
  };

  const renderProgressCard = () => {
    if (isAnnounced(project.status)) {
      const debtPriceAmount = project.accounting.debtPrice.amount;
      const reservedAmount = project.accounting.reservedSum?.amount ?? 0;
      const limitAmount = project.reservationLimit?.amount ?? 0;

      const percentage =
        debtPriceAmount > 0 ? parseFloat((reservedAmount / debtPriceAmount).toFixed(2)) * 100 : 0;
      const markerPercentage =
        debtPriceAmount > 0 && limitAmount > 0
          ? parseFloat((limitAmount / debtPriceAmount).toFixed(2)) * 100
          : undefined;

      const daysToOpen = project.plannedCollectionDate
        ? Math.max(0, moment(project.plannedCollectionDate).diff(moment(), 'days'))
        : null;

      return (
        <div className={styles.detailedProjectMain}>
          <div className={styles.detailedProjectMain__progressContainer}>
            <ProjectProgress
              percentage={percentage}
              markerPercentage={markerPercentage}
              variant="reservation"
            />
          </div>
          <div className={styles.detailedProjectMain__statsBlock}>
            {project.reservationLimit && (
              <div className={styles.detailedProjectMain__statRow}>
                <span>Лимит резервирования:</span>
                <span>
                  <Money money={project.reservationLimit} />
                </span>
              </div>
            )}
            {project.accounting.reservedSum && (
              <div className={styles.detailedProjectMain__statRow}>
                <span>Зарезервировано:</span>
                <span>
                  <Money money={project.accounting.reservedSum} />
                </span>
              </div>
            )}
            {daysToOpen !== null && (
              <div className={styles.detailedProjectMain__statRow}>
                <span>До открытия инвестирования:</span>
                <span className={styles.detailedProjectMain__days}>{formatDays(daysToOpen)}</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (isFundRaising(project.status)) {
      const percentage = calculateCollectedPercentage(
        project.accounting.investedSum,
        project.accounting.debtPrice
      );
      const reservedAmount = project.accounting.reservedSum?.amount ?? 0;
      const available = new MoneyModel(
        Math.max(
          0,
          project.accounting.debtPrice.amount -
            project.accounting.investedSum.amount -
            reservedAmount
        ),
        project.accounting.debtPrice.currencyCode
      );
      const daysToEnd = Math.max(0, moment(project.accounting.maturityDate).diff(moment(), 'days'));

      const reservationPercentage =
        reservedAmount > 0 && project.accounting.debtPrice.amount > 0
          ? parseFloat((reservedAmount / project.accounting.debtPrice.amount).toFixed(4)) * 100
          : undefined;

      return (
        <div className={styles.detailedProjectMain}>
          <div className={styles.detailedProjectMain__progressContainer}>
            <ProjectProgress
              percentage={percentage}
              reservationPercentage={reservationPercentage}
            />
          </div>
          <div className={styles.detailedProjectMain__statsBlock}>
            <div className={styles.detailedProjectMain__statRow}>
              <span>Инвестировано:</span>
              <span>
                <Money money={project.accounting.investedSum} />
              </span>
            </div>
            {project.accounting.reservedSum && reservedAmount > 0 && (
              <div className={styles.detailedProjectMain__statRow}>
                <span>Зарезервировано:</span>
                <span>
                  <Money money={project.accounting.reservedSum} />
                </span>
              </div>
            )}
            <div className={styles.detailedProjectMain__statRow}>
              <span>Доступно для инвестирования:</span>
              <span>
                <Money money={available} />
              </span>
            </div>
            <div className={styles.detailedProjectMain__statRow}>
              <span>До завершения сбора средств:</span>
              <span>{formatDays(daysToEnd)}</span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {renderProgressCard()}
      {!isMobile ? (
        <div className={styles.detailedProjectMain}>
          <div className={styles.detailedProjectMain__stats}>
            <div className={styles.detailedProjectStat__title}>План</div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__type}>Сумма сбора</div>
              <div className={styles.detailedProjectStat__value}>
                <Money money={project.accounting.debtPrice} />
              </div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__type}>Взыскание</div>
              <div className={styles.detailedProjectStat__value}>
                <Money money={recoveryForecast} />
              </div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__type}>Срок</div>
              <div className={styles.detailedProjectStat__value}>
                {project.accounting.initialTerm} мес.
              </div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__type}>ROI</div>
              <div className={styles.detailedProjectStat__value}>
                {formatPercents(project.accounting.simpleInterestRate)}
              </div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__type}>Годовой ROI</div>
              <div className={styles.detailedProjectStat__value}>
                {formatPercents(project.accounting.forecastYearROI)}
              </div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__type}>Годовой IRR</div>
              <div className={styles.detailedProjectStat__value}>
                {formatPercents(project.accounting.interestRate)}
              </div>
            </div>
          </div>
          <div className={styles.detailedProjectMain__stats}>
            <div className={styles.detailedProjectStat__title}>Факт</div>
            <div className={styles.detailedProjectStat}></div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__value}>
                {isLoanPaymentPaid && project.accounting.actualPayment ? (
                  <Money money={project.accounting.actualPayment} />
                ) : (
                  '-'
                )}
              </div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__value}>
                {isLoanPaymentPaid && project.accounting.realTerm
                  ? `${project.accounting.realTerm} мес.`
                  : '-'}
              </div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__value}>
                {isPaid && project.accounting.factROI
                  ? formatPercents(project.accounting.factROI)
                  : '-'}
              </div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__value}>
                {isPaid && project.accounting.factYearROI
                  ? formatPercents(project.accounting.factYearROI)
                  : '-'}
              </div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__value}>
                {isPaid && project.accounting.factYearIRR
                  ? formatPercents(project.accounting.factYearIRR)
                  : '-'}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.detailedProjectMain}>
          <div className={styles.detailedProjectMain__stats}>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__type}></div>
              <div className={styles.detailedProjectStat__title}>План</div>
              <div className={styles.detailedProjectStat__title}>Факт</div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__type}>Сумма сбора</div>
              <div className={styles.detailedProjectStat__value}>
                <Money money={project.accounting.debtPrice} />
              </div>
              <div className={styles.detailedProjectStat__value}></div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__type}>Взыскание</div>
              <div className={styles.detailedProjectStat__value}>
                <Money money={recoveryForecast} />
              </div>
              <div className={styles.detailedProjectStat__value}>
                {isLoanPaymentPaid && project.accounting.actualPayment ? (
                  <Money money={project.accounting.actualPayment} />
                ) : (
                  '-'
                )}
              </div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__type}>Срок</div>
              <div className={styles.detailedProjectStat__value}>
                {project.accounting.initialTerm} мес.
              </div>
              <div className={styles.detailedProjectStat__value}>
                {isLoanPaymentPaid && project.accounting.realTerm
                  ? `${project.accounting.realTerm} мес.`
                  : '-'}
              </div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__type}>ROI</div>
              <div className={styles.detailedProjectStat__value}>
                {formatPercents(project.accounting.simpleInterestRate)}
              </div>
              <div className={styles.detailedProjectStat__value}>
                {isPaid && project.accounting.factROI
                  ? formatPercents(project.accounting.factROI)
                  : '-'}
              </div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__type}>Годовой ROI</div>
              <div className={styles.detailedProjectStat__value}>
                {formatPercents(project.accounting.forecastYearROI)}
              </div>
              <div className={styles.detailedProjectStat__value}>
                {isPaid && project.accounting.factYearROI
                  ? formatPercents(project.accounting.factYearROI)
                  : '-'}
              </div>
            </div>
            <div className={styles.detailedProjectStat}>
              <div className={styles.detailedProjectStat__type}>Годовой IRR</div>
              <div className={styles.detailedProjectStat__value}>
                {formatPercents(project.accounting.interestRate)}
              </div>
              <div className={styles.detailedProjectStat__value}>
                {isPaid && project.accounting.factYearIRR
                  ? formatPercents(project.accounting.factYearIRR)
                  : '-'}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectMainStats;
